import { Link } from 'react-router-dom';
import { RegistroCta } from '@/features/auth/components/registro-cta';
import { motion } from 'motion/react';
import { ArrowRight, ShieldCheck, Target, Brain, Sparkles, ChevronLeft } from 'lucide-react';
import { ScrollReveal } from '@/components/motion/scroll-reveal';
import { LandingNavbar } from '@/features/landing/components/landing-navbar';
import { BrandColorCard } from '../components/brand-color-card';
import { ValueCard } from '../components/value-card';
import { BRAND_PALETTE } from '@/lib/utils/brand';

// La paleta vive en `lib/utils/brand.ts`, que es también de donde salen los
// tokens de Tailwind (`bg-prixia-*`). Un solo lugar para los hexes.
const brandColors = BRAND_PALETTE;

const values = [
  {
    icon: ShieldCheck,
    title: 'Confianza',
    description:
      'Construimos relaciones basadas en transparencia y resultados medibles. Cada precio que calculas con Prixia esta respaldado por datos.',
    gradient: 'from-emerald-500 to-teal-400',
  },
  {
    icon: Target,
    title: 'Precision',
    description:
      'Cada decimal cuenta. Nuestras formulas dinamicas y modelos de pricing eliminan el error humano y maximizan tus márgenes.',
    gradient: 'from-blue-500 to-cyan-400',
  },
  {
    icon: Brain,
    title: 'Inteligencia',
    description:
      'No solo calculamos precios — los entendemos. Nuestra IA analiza patrones, sugiere estrategias y aprende de tu negocio.',
    gradient: 'from-violet-500 to-purple-400',
  },
  {
    icon: Sparkles,
    title: 'Innovacion',
    description:
      'Pioneros en combinar pricing science con inteligencia artificial. Transformamos la forma en que las empresas piensan sobre sus precios.',
    gradient: 'from-amber-500 to-orange-400',
  },
];

export function WhyPrixiaPage() {
  return (
    <div className="min-h-screen text-gray-900 dark:text-gray-100 overflow-x-hidden bg-gradient-to-br from-white via-blue-50/40 to-violet-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      {/* Background blobs */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-40 w-[600px] h-[600px] bg-[#1B3A5C]/15 dark:bg-[#1B3A5C]/10 rounded-full blur-[128px] animate-float" />
        <div
          className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-[#D4A843]/15 dark:bg-[#D4A843]/8 rounded-full blur-[128px] animate-float"
          style={{ animationDelay: '2s' }}
        />
        <div
          className="absolute bottom-0 left-1/3 w-[400px] h-[400px] bg-[#6BAED6]/12 dark:bg-[#6BAED6]/5 rounded-full blur-[128px] animate-float"
          style={{ animationDelay: '4s' }}
        />
      </div>

      <LandingNavbar />

      {/* ─── Hero ─────────────────────────────────────────────── */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 overflow-hidden">
        {/* Hero gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#1B3A5C]/5 via-transparent to-transparent dark:from-[#1B3A5C]/20" />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors mb-8"
            >
              <ChevronLeft className="w-4 h-4" />
              Volver al inicio
            </Link>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1B3A5C] via-[#2A5A8C] to-[#6BAED6] dark:from-[#6BAED6] dark:via-[#A8B5C4] dark:to-[#D4A843]">
                ¿Por que Prixia?
              </span>
            </h1>
            <p className="text-xl sm:text-2xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed">
              La historia detras de nuestro nombre
            </p>
          </motion.div>

          {/* Decorative line */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1.2, delay: 0.4, ease: 'easeOut' }}
            className="mt-12 h-px bg-gradient-to-r from-transparent via-[#D4A843]/50 to-transparent max-w-lg mx-auto"
          />
        </div>
      </section>

      {/* ─── Origin of the name ───────────────────────────────── */}
      <section className="py-20 sm:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                El origen del nombre
              </h2>
              <p className="text-gray-500 dark:text-gray-400 max-w-xl mx-auto">
                Seis letras que encapsulan nuestra mision
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Prix */}
            <ScrollReveal delay={0.1}>
              <div className="relative group">
                <div className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-[#1B3A5C] to-[#2A5A8C] opacity-0 group-hover:opacity-100 blur-lg transition-opacity duration-500" />
                <div className="relative bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-8 h-full">
                  <div className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-br from-[#1B3A5C] to-[#2A5A8C] mb-4">
                    Prix
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                    Del latin/frances{' '}
                    <span className="font-semibold text-gray-700 dark:text-gray-300">
                      &laquo;prix&raquo;
                    </span>{' '}
                    (precio) — la raiz etimologica de "precio" en multiples idiomas: <em>prix</em>{' '}
                    en frances, <em>prezzo</em> en italiano, <em>precio</em> en espanol. Representa
                    nuestra esencia:{' '}
                    <strong className="text-[#1B3A5C] dark:text-[#6BAED6]">
                      inteligencia de precios.
                    </strong>
                  </p>
                </div>
              </div>
            </ScrollReveal>

            {/* IA */}
            <ScrollReveal delay={0.2}>
              <div className="relative group">
                <div className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-[#D4A843] to-[#6BAED6] opacity-0 group-hover:opacity-100 blur-lg transition-opacity duration-500" />
                <div className="relative bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-8 h-full">
                  <div className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-br from-[#D4A843] to-[#6BAED6] mb-4">
                    IA
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                    Las ultimas dos letras son un guino directo a la{' '}
                    <span className="font-semibold text-gray-700 dark:text-gray-300">
                      Inteligencia Artificial
                    </span>
                    , el motor que potencia nuestra plataforma. No es coincidencia:{' '}
                    <strong className="text-[#D4A843]">la IA esta en nuestro ADN.</strong>
                  </p>
                </div>
              </div>
            </ScrollReveal>

            {/* Prixia */}
            <ScrollReveal delay={0.3}>
              <div className="relative group">
                <div className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-[#2A5A8C] to-[#D4A843] opacity-0 group-hover:opacity-100 blur-lg transition-opacity duration-500" />
                <div className="relative bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-8 h-full">
                  <div className="text-5xl font-black mb-4">
                    <span className="text-transparent bg-clip-text bg-gradient-to-br from-[#1B3A5C] to-[#2A5A8C]">
                      Prix
                    </span>
                    <span className="text-transparent bg-clip-text bg-gradient-to-br from-[#D4A843] to-[#6BAED6]">
                      ia
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                    Suena premium, es fácil de recordar, funciona en cualquier idioma y encapsula en
                    6 letras lo que hacemos:{' '}
                    <strong className="text-[#2A5A8C] dark:text-[#6BAED6]">
                      pricing + inteligencia artificial.
                    </strong>
                  </p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ─── Values ───────────────────────────────────────────── */}
      <section className="py-20 sm:py-28 bg-gray-50/50 dark:bg-gray-900/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Nuestros valores
              </h2>
              <p className="text-gray-500 dark:text-gray-400 max-w-xl mx-auto">
                Los pilares que guian cada decision en Prixia
              </p>
            </div>
          </ScrollReveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, i) => (
              <ScrollReveal key={value.title} delay={i * 0.1}>
                <ValueCard {...value} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Brand palette ────────────────────────────────────── */}
      <section className="py-20 sm:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Nuestra paleta de colores
              </h2>
              <p className="text-gray-500 dark:text-gray-400 max-w-xl mx-auto">
                Cada color cuenta una parte de nuestra historia
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6">
            {brandColors.map((color, i) => (
              <ScrollReveal key={color.hex} delay={i * 0.08}>
                <BrandColorCard {...color} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Logo section ─────────────────────────────────────── */}
      <section className="py-20 sm:py-28 bg-gray-50/50 dark:bg-gray-900/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                El logo
              </h2>
              <p className="text-gray-500 dark:text-gray-400 max-w-xl mx-auto">
                Geometria, datos e inteligencia en un solo símbolo
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.15}>
            <div className="relative group max-w-md mx-auto">
              <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-[#1B3A5C]/20 to-[#D4A843]/20 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              <div className="relative bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-12 flex items-center justify-center">
                <img
                  src="/prixia-logo.svg"
                  alt="Logo de Prixia"
                  className="h-16 sm:h-20 w-auto object-contain"
                />
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.25}>
            <p className="mt-8 text-center text-sm sm:text-base text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed">
              Nuestro logo combina un{' '}
              <strong className="text-gray-700 dark:text-gray-300">hexagono geometrico</strong> —
              simbolo de estructura, datos y conexion — con una{' '}
              <strong className="text-gray-700 dark:text-gray-300">
                línea de tendencia ascendente
              </strong>{' '}
              que representa el crecimiento de nuestros clientes. Los nodos neurales en los vertices
              reflejan la inteligencia artificial que impulsa cada decision de pricing.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* ─── CTA ──────────────────────────────────────────────── */}
      <section className="py-20 sm:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="relative overflow-hidden rounded-2xl">
              <div className="absolute inset-0 bg-gradient-to-br from-[#1B3A5C] via-[#2A5A8C] to-[#1B3A5C]" />
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(212,168,67,0.15),_transparent_50%)]" />

              <div className="relative px-8 py-16 sm:px-16 sm:py-20 text-center">
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  Descubri lo que Prixia puede hacer por tu negocio
                </h2>
                <p className="text-white/60 max-w-xl mx-auto mb-8">
                  Pricing inteligente, impulsado por IA, listo para tu empresa.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <RegistroCta className="group flex items-center gap-2 px-8 py-3.5 rounded-xl bg-[#D4A843] text-[#1B3A5C] font-semibold hover:bg-[#D4A843]/90 transition-all hover:shadow-[0_0_30px_rgba(212,168,67,0.3)]">
                    Comenzar ahora
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </RegistroCta>
                  <Link
                    to="/#features"
                    className="flex items-center gap-2 px-8 py-3.5 rounded-xl border border-white/20 text-white hover:bg-white/10 transition-all"
                  >
                    Ver caracteristicas
                  </Link>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ─── Mini footer ──────────────────────────────────────── */}
      <footer className="border-t border-gray-200/70 dark:border-gray-800 py-8 bg-white/50 dark:bg-gray-950/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-gray-400 dark:text-gray-500">
            &copy; {new Date().getFullYear()} Prixia. Todos los derechos reservados.
          </p>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            >
              Inicio
            </Link>
            <Link
              to="/privacy"
              className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            >
              Privacidad
            </Link>
            <Link
              to="/terms"
              className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            >
              Terminos
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
